import { database } from '@app/firebase';
import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import HomesCarousel from '@components/carousels/HomesCarousel';
import Testimonials from '@components/testimonials/Testimonials';
import FooterContext, { defaultFooterContext } from '@context/FooterContext';
import { PHOENIX_PHONE_NUMBER } from '@data/src/phoneNumbers';
import { ContentBlock } from '@layouts/ContentBlocks';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { makeSeoData } from 'config/next-seo.config';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { FAQPageJsonLd, NextSeo } from 'next-seo';
import React, { useContext, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';

import HowItWorks from '../../../components/blocks/HowItWorks';
import { CTAButton } from '../../../components/buttons/CTAButton';
import HeroAlt from '../../../components/hero-alt/HeroAlt';
import useStyles from '../Stage.style';

const title = 'Home Staging in Phoenix | Guest House Professional Staging';
const description = 'Professional home staging in Phoenix that helps you sell for thousands more. Our designers & products make the difference.';
const image = '/images/home-staging/stage-steps-bg.jpeg';

export const getServerSideProps: GetServerSideProps = async () => {
  const homes = await getDocs(query(collection(database, 'homes')
    ,where('published', '==', true)
    ,where('featured', '==', true)
    ,orderBy('install_date', 'desc')
    ,limit(10)));

  const data = {
    homes: JSON.stringify(homes.docs.map(h => h.data())),
  };

  return { props: { data } };
};

const faqs = [
  {
    q: (
      <>
        How soon could I stage a home?
      </>
    ),
    a: (
      <>
        We currently operate on two week lead times from the date of purchase. With that said &mdash; if you have a tighter timeline, get a quote from us and we&apos;ll see what we can do.
      </>
    ),
  },
  {
    q: (
      <>
        Which rooms do you typically stage?
      </>
    ),
    a: (
      <>
        Our staging package includes the living room, dining room, kitchen, primary bedroom and primary bathroom. More rooms can be added easily for a small additional fee.
      </>
    ),
  },
  {
    q: (
      <>
        Do I get to choose the furnishings?
      </>
    ),
    a: (
      <>
        With each project, our interior designer sends you a detailed moodboard that includes furniture, art and decor based on your desired style. If you want to change any items, just let them know and they will quickly show more options. We&apos;re all about collaboration and transparency.
      </>
    ),
  },
  {
    q: (
      <>
        How quickly will I receive photos?
      </>
    ),
    a: (
      <>
        The day after we stage the home, we photograph with our team. We then send you final images within one business day.
      </>
    ),
  },
];

function Stage(props) {
  const { classes, theme } = useStyles();
  const { md } = useBreakpoints();
  const homes = JSON.parse(props.data.homes) as guesthouse.Home[];
  const footerContext = useContext<FooterContext>(FooterContext);

  useEffect(() => {
    footerContext.setContext({
      ...footerContext,
      phoneNumberHref: `tel:1-${PHOENIX_PHONE_NUMBER}`,
      phoneNumberText: `${PHOENIX_PHONE_NUMBER}`,
    });

    return () => footerContext.setContext({
      ...footerContext,
      phoneNumberText: defaultFooterContext.phoneNumberText,
      phoneNumberHref: defaultFooterContext.phoneNumberHref,
    });
  }, []);


  return (
    <>
      <AppBackgroundColor
        backgroundColor="#F7F7F7"
        borderColor="#F7F7F7"
        headerColor="#F7F7F7"
      />
      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description, image })} />

      <FAQPageJsonLd
        mainEntity={faqs.map(faq => {
          return {
            questionName: ReactDOMServer.renderToString(faq.q),
            acceptedAnswerText: ReactDOMServer.renderToString(faq.a),
          };
        })}
      />
      
      <HeroAlt 
        title={
          <>
            Beautiful Phoenix staging,
            <br />
            {' '}
            delivered faster than ever
          </>
          }
        subText={
          <>
          </>
          }
        backgroundImage="/images/home-staging/phoenix/hero.jpg"
        ctaButtons={[
          { 
            label: 'Get an instant quote',
            href: '/home-staging/get-a-quote',
            variant: 'contained',
            color: 'primary',
          },
          { 
            icon: <LocalPhoneIcon />,
            label: 'Call us',
            href: `tel:1-${PHOENIX_PHONE_NUMBER}`,
            variant: 'contained',
            color: 'secondary',
            target: '_blank',
          },
        ]}
      />

      <ContentBlock
        maxWidth={655}
        margin={md ? ['115px', 'auto'] : ['40px', 'auto']}
      >
        <Typography
          fontFamily={theme.gh_vars.circular}
          variant="body1"
          style={{ fontSize: md ? 24 : 18,textAlign: 'center' }}
        >
          Guest House turns every property into a standout
          <br />
          listing — helping agents and homeowners across Phoenix sell faster and earn more. 
        </Typography>
      </ContentBlock>

      <ContentBlock
        maxWidth={1365}
        margin={[0, 0]}
        style={{ marginTop: theme.spacing(11) }}
      >
        <Typography
          gutterBottom
          variant="h2"
          style={{ textAlign: 'center', fontSize: md ? 36 : 24, marginBottom: md ? theme.spacing(7.5) : theme.spacing(4) }}
        >
          You focus on selling. We&nbsp;handle&nbsp;the&nbsp;rest.
        </Typography>

        <Grid
          container
          spacing={6}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Box textAlign="center">
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  width: '100%',
                  height: md ? 360 : 256,
                  borderRadius: theme.shape.borderRadius
                }}
              >
                <Image
                  fill
                  src="/images/home-staging/premium-brands.png"
                  alt=""
                  sizes="100vw"
                  style={{
                    objectFit: 'cover'
                  }}
                />
              </div>

              <Typography
                variant="h3Alt"
                style={{ fontSize: 18, marginTop: theme.spacing(3), marginBottom: theme.spacing(3), textAlign: 'left' }}
              >
                Book staging, cleaning, photos & more
              </Typography>

              <Typography
                className={classes.gridCopy}
                sx={{ '& a': { color: '#080808' } }}
              >
                We make it easy to book and schedule market-ready services. Checkout with credit card or 
                <Link href="/affirm">
                  {' '}
                  pay over time with Affirm
                </Link>
                .
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Box textAlign="center">
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  width: '100%',
                  height: md ? 360 : 256,
                  borderRadius: theme.shape.borderRadius
                }}
              >
                <Image
                  fill
                  src="/images/home-staging/professional-designers.png"
                  alt=""
                  sizes="100vw"
                  style={{
                    objectFit: 'cover'
                  }}
                />
              </div>

              <Typography
                variant="h3Alt"
                style={{ fontSize: 18, marginTop: theme.spacing(3), marginBottom: theme.spacing(3), textAlign: 'left' }}
              >
                Get support from our local team            
              </Typography>

              <Typography className={classes.gridCopy}>
                Our market team works directly with you to deliver a beautiful listing from start-to-finish.
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Box textAlign="center">
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  width: '100%',
                  height: md ? 360 : 256,
                  borderRadius: theme.shape.borderRadius
                }}
              >
                <Image
                  fill
                  src="/images/home-staging/phoenix/additional-services.jpg"
                  alt=""
                  sizes="100vw"
                  style={{
                    objectFit: 'cover'
                  }}
                />
              </div>

              <Typography
                variant="h3Alt"
                style={{ fontSize: 18, marginTop: theme.spacing(3), marginBottom: theme.spacing(3), textAlign: 'left' }}
              >
                Prep now, pay later 
              </Typography>

              <Typography className={classes.gridCopy}>
                Choose from flexible financing options to defer payment and help with cash flow.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        margin={[4, 0]}
      >
        <Grid
          container
          justifyContent="center"
        >
          <Grid
            item
            xs={12}
            style={{ textAlign: 'center', marginBottom: theme.spacing(2) }}
          >
            <CTAButton
              href="/home-staging/get-a-quote"
              label="Get an instant quote"
              color="primary"
            />
          </Grid>
          <Grid
            item
            xs={12}
            style={{ textAlign: 'center' }}
          >
            <Typography
              gutterBottom
              fontFamily={theme.gh_vars.circular}
              style={{ marginTop: theme.spacing(1.5), fontSize: 20, display: 'inline-block', fontWeight: 700 }}
            >
              Give us a call: 
              {' '}
              {' '}
              <a
                href={footerContext.phoneNumberHref}
                style={{ color: '#83A5B0' }}
              >
                {footerContext.phoneNumberText}
              </a> 
            </Typography>
     
          </Grid>
        </Grid>
      </ContentBlock>

      <ContentBlock
        margin={[0]}
        padding={[4, 0, 4, 0]}
        paddingMd={[15, 0, 15, 0]}
        style={{
          backgroundColor: '#CCD5D6'
        }}
        maxWidth={'none'}
      >
        <ContentBlock
          margin={[0, 'auto']}
          
          maxWidth={1308}
        >
          <Typography
            variant="body1"
            component="h2"
            style={{ fontSize: md ? 20 : 16,marginBottom: 8 }}
          >
            Reviews
          </Typography>
          <Typography
            variant="h4Alt"
            style={{ fontSize: md ? 48 : 28, marginBottom: md ? theme.spacing(5) : theme.spacing(3) }}
          >
            What customers are saying
          </Typography>
         
          <Testimonials
            backgroundColor="#ffffff"
            location="denver"
          />
         
        </ContentBlock>
      </ContentBlock>

      <HowItWorks />

      <ContentBlock
        maxWidth={'none'}
        style={{ backgroundColor: '#f6f6f7', overflowX: 'hidden' }}
        padding={[4, 0, 6, 0]}
        paddingMd={[12, 0, 12, 0]}
        margin={0}
      >
        <ContentBlock
          maxWidth={1308}
          margin={[0, 'auto']}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              '& a': {
                textDecoration: 'none !important'
              }
            }}
          >
            <Box>
              <Typography
                variant="h4Alt"
                style={{ fontSize: md ? 48 : 28, marginBottom: 8 }}
              >
                Recent listings
                {' '}
              </Typography>
         
              <Typography
                variant="body1"
                component="h2"
                style={{ fontSize: md ? 20 : 16, marginBottom: 60 }}
              >
                Check out our work in Colorado, California, Arizona & beyond
                {' '}
              </Typography>
            </Box>
            <CTAButton
              href="/shop/homes"
              label="See all listings"
              color="primary"
            />
          </Box>

          {!!homes?.length && (
            <HomesCarousel
              seeAll
              homes={homes}
            />
          )}
        </ContentBlock>
      </ContentBlock>
    </>
  );
}
export default Stage;
